import { useEffect, useMemo, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtBRL, fmtNum, fmtDate } from "@/lib/format";
import type { NotaSimplificada } from "@/lib/fiscal-types";
import {
  ChevronLeft, ChevronRight, Download, FileDown, X, Copy, Check, FileText,
  ClipboardList, Hash, Calendar, FileSignature, Tag, Workflow, ArrowRightLeft,
  User, Users, KeyRound, CircleDollarSign, ShieldCheck, Code2, Fingerprint, MapPin, Send,
  Receipt
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { StatusBadge } from "@/components/StatusBadge";

interface Props {
  notas: NotaSimplificada[];
  index: number | null;
  onClose: () => void;
  onChangeIndex: (i: number) => void;
}

export function NotaDetailModal({ notas, index, onClose, onChangeIndex }: Props) {
  const open = index !== null;
  const nota = open && index !== null ? notas[index] : null;
  const [loading, setLoading] = useState(false);
  const [cache] = useState(() => new Set<string>());
  const [copied, setCopied] = useState(false);

  const tableHoverRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!nota) return;
    if (cache.has(nota.chave)) { setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(() => { cache.add(nota.chave); setLoading(false); }, 120);
    return () => clearTimeout(t);
  }, [nota, cache]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();

      // Se o mouse estiver sobre a tabela, rola a tabela em vez de mudar a nota
      if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && tableHoverRef.current && scrollContainerRef.current) {
        e.preventDefault();
        const amount = 350; // Quantidade de rolagem por toque na seta
        if (e.key === "ArrowLeft") {
          scrollContainerRef.current.scrollBy({ left: -amount, behavior: "smooth" });
        } else {
          scrollContainerRef.current.scrollBy({ left: amount, behavior: "smooth" });
        }
        return;
      }

      if (e.key === "ArrowLeft" && index !== null && index > 0) onChangeIndex(index - 1);
      if (e.key === "ArrowRight" && index !== null && index < notas.length - 1) onChangeIndex(index + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, notas.length, onChangeIndex, onClose]);

  function copyChave() {
    if (!nota) return;
    navigator.clipboard.writeText(nota.chave).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  function exportarPDF() {
    if (!nota) return;

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const m = 15;
    const contentW = pageWidth - (m * 2);

    const drawField = (x: number, currY: number, w: number, h: number, label: string, value: string | number, align: "left" | "right" | "center" = "left", bold = true, r = 3) => {
      doc.setDrawColor(0); // Preto para bordas
      doc.setLineWidth(0.6);
      doc.roundedRect(x, currY, w, h, r, r);
      
      // Rótulo (Label)
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80);
      doc.text(label.toUpperCase(), x + 3, currY + 7);
      
      // Valor (Data)
      doc.setFontSize(7);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setTextColor(0);
      const textX = align === "right" ? x + w - 3 : align === "center" ? x + (w / 2) : x + 3;
      const valStr = String(value || "");
      const valY = currY + 17; 
      doc.text(valStr, textX, valY, { align, maxWidth: w - 5 });
    };

    const drawSectionTitle = (title: string, currY: number) => {
      doc.setFillColor(245, 245, 245);
      doc.setDrawColor(0);
      doc.setLineWidth(0.6);
      doc.roundedRect(m, currY - 15, contentW, 12, 2, 2, "FD");
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0);
      doc.text(title.toUpperCase(), m + 5, currY - 6.5);
    };

    const renderHeader = (pageNum: number, totalPages: number, initialY = m) => {
      let currY = initialY;
      doc.setDrawColor(0);


      // BOXES PRINCIPAIS
      const hH = 95;
      // Emitente
      doc.roundedRect(m, currY, contentW * 0.42, hH, 3, 3);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(nota.emitente || "", m + (contentW * 0.21), currY + 20, { align: "center", maxWidth: contentW * 0.38 });
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      const endE = nota.detalhes?.emitente?.endereco;
      if (endE) {
        doc.text(`${endE.xLgr}, ${endE.nro} - ${endE.xBairro}`, m + (contentW * 0.21), currY + 45, { align: "center" });
        doc.text(`CEP: ${endE.CEP} - ${endE.xMun} - ${endE.UF}`, m + (contentW * 0.21), currY + 55, { align: "center" });
        doc.text(`Fone/Fax: ${endE.fone || ""}`, m + (contentW * 0.21), currY + 65, { align: "center" });
      }

      // DANFE
      doc.roundedRect(m + (contentW * 0.425), currY, contentW * 0.16, hH, 1, 1);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("DANFE", m + (contentW * 0.505), currY + 15, { align: "center" });
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text("Documento Auxiliar da\nNota Fiscal Eletrônica", m + (contentW * 0.505), currY + 24, { align: "center" });

      doc.setFontSize(7);
      doc.text((nota.detalhes?.ide as any)?.tpNF === "0" ? "0 - ENTRADA" : "1 - SAÍDA", m + (contentW * 0.435), currY + 48);
      doc.rect(m + (contentW * 0.54), currY + 40, 15, 12);
      doc.setFont("helvetica", "bold");
      doc.text((nota.detalhes?.ide as any)?.tpNF === "0" ? "0" : "1", m + (contentW * 0.54) + 7.5, currY + 49, { align: "center" });

      doc.setFontSize(8);
      doc.text(`Nº ${nota.numero}`, m + (contentW * 0.505), currY + 65, { align: "center" });
      doc.text(`SÉRIE ${nota.serie}`, m + (contentW * 0.505), currY + 75, { align: "center" });
      doc.text(`Folha ${pageNum}/${totalPages}`, m + (contentW * 0.505), currY + 85, { align: "center" });

      // CHAVE / BARCODE
      doc.roundedRect(m + (contentW * 0.59), currY, contentW * 0.41, hH, 1, 1);
      doc.setFillColor("#000000");
      let bX = m + (contentW * 0.59) + 10;
      const bEndX = m + contentW - 10;
      while (bX < bEndX) {
        const bw = Math.random() * 2.5 + 0.8;
        if (bX + bw > bEndX) break;
        doc.rect(bX, currY + 5, bw, 33, "F");
        bX += bw + (Math.random() * 1.5 + 0.5);
      }
      // Ajuste na altura da chave para evitar sobreposição
      drawField(m + (contentW * 0.59), currY + 40, contentW * 0.41, 26, "Chave de Acesso", formatChave(nota.chave), "left", true);
      doc.setFontSize(6);
      doc.text("Consulta de autenticidade no portal nacional da NF-e", m + (contentW * 0.59) + 5, currY + 74);
      doc.text("www.nfe.fazenda.gov.br/portal ou no site da Sefaz Autorizadora", m + (contentW * 0.59) + 5, currY + 82);

      currY += hH + 2;
      drawField(m, currY, contentW * 0.55, 22, "Natureza da Operação", (nota.natOp || "").slice(0, 70), "left");
      drawField(m + (contentW * 0.555), currY, contentW * 0.445, 22, "Protocolo de Autorização de Uso", (meta.protocolo || "") + " - " + (meta.dhRecbto ? formatDh(meta.dhRecbto) : ""), "left");

      currY += 24;
      drawField(m, currY, contentW * 0.25, 22, "Inscrição Estadual", nota.detalhes?.emitente?.IE || "", "left");
      drawField(m + (contentW * 0.255), currY, contentW * 0.25, 22, "Insc. Est. do Subst. Trib.", "", "left");
      drawField(m + (contentW * 0.51), currY, contentW * 0.49, 22, "CNPJ", nota.cnpjEmit, "left");

      return currY + 40;
    };

    const renderCanhoto = (y: number) => {
      const h = 35;
      const w1 = contentW * 0.84;
      const w2 = contentW * 0.15;
      const gap = contentW * 0.01;

      doc.setLineWidth(0.6);
      doc.setDrawColor(0);
      // Caixa grande
      doc.roundedRect(m, y, w1, h, 2, 2);
      doc.setFontSize(6);
      doc.setFont("helvetica", "bold");
      doc.text(`RECEBEMOS DE ${nota.emitente || ""} OS PRODUTOS CONSTANTES DA NOTA FISCAL INDICADA AO LADO`, m + 3, y + 9, { maxWidth: w1 - 10 });
      
      doc.line(m, y + 14, m + w1, y + 14);
      doc.line(m + (w1 * 0.25), y + 14, m + (w1 * 0.25), y + h);
      
      doc.setFontSize(5);
      doc.setFont("helvetica", "normal");
      doc.text("DATA DE RECEBIMENTO", m + 3, y + 18.5);
      doc.text("IDENTIFICAÇÃO E ASSINATURA DO RECEBEDOR", m + (w1 * 0.25) + 3, y + 18.5);

      // Caixa pequena NF-e
      doc.roundedRect(m + w1 + gap, y, w2, h, 2, 2);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      doc.text("NF-e", m + w1 + gap + (w2/2), y + 10, { align: "center" });
      doc.setFontSize(8);
      doc.text(`Nº ${nota.numero}`, m + w1 + gap + (w2/2), y + 20, { align: "center" });
      doc.setFontSize(7);
      doc.text(`SÉRIE ${nota.serie}`, m + w1 + gap + (w2/2), y + 30, { align: "center" });

      // Linha tracejada
      (doc as any).setLineDash?.([2, 2]);
      doc.line(m, y + h + 8, m + contentW, y + h + 8);
      (doc as any).setLineDash?.([]);
      
      return y + h + 15;
    };

    let startY = renderCanhoto(15);
    startY = renderHeader(1, 1, startY);

    // DESTINATÁRIO
    drawSectionTitle("DESTINATÁRIO / REMETENTE", startY);
    const dst = nota.detalhes?.destinatario;
    const ed = dst?.endereco;
    drawField(m, startY, contentW * 0.61, 22, "Nome / Razão Social", nota.destinatario, "left");
    drawField(m + (contentW * 0.615), startY, contentW * 0.21, 22, "CNPJ / CPF", nota.cnpjDest, "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Data Emissão", dataEmissaoCurta, "right");
    startY += 25;
    drawField(m, startY, contentW * 0.48, 22, "Endereço", `${ed?.xLgr || ""}, ${ed?.nro || ""}`, "left");
    drawField(m + (contentW * 0.485), startY, contentW * 0.23, 22, "Bairro / Distrito", ed?.xBairro || "", "left");
    drawField(m + (contentW * 0.72), startY, contentW * 0.105, 22, "CEP", ed?.CEP || "", "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Data Saída", dataEmissaoCurta, "right");
    startY += 25;
    drawField(m, startY, contentW * 0.36, 22, "Município", ed?.xMun || "", "left");
    drawField(m + (contentW * 0.365), startY, contentW * 0.045, 22, "UF", ed?.UF || "", "center");
    drawField(m + (contentW * 0.415), startY, contentW * 0.15, 22, "Fone / Fax", ed?.fone || "", "left");
    drawField(m + (contentW * 0.57), startY, contentW * 0.255, 22, "Inscrição Estadual", dst?.IE || "", "left");
    drawField(m + (contentW * 0.83), startY, contentW * 0.17, 22, "Hora Saída", "00:00:00", "right");
    startY += 40;

    // FATURA / DUPLICATA
    drawSectionTitle("FATURA / DUPLICATA", startY);
    const fatW = contentW / 3;
    drawField(m, startY, fatW - 2, 22, "Número", "001", "left");
    drawField(m + fatW, startY, fatW - 2, 22, "Vencimento", dataEmissaoCurta, "left");
    drawField(m + (fatW * 2), startY, fatW, 22, "Valor", fmtBRL(nota.valorOficial), "right");
    startY += 40;

    // CÁLCULO DO IMPOSTO
    drawSectionTitle("CÁLCULO DO IMPOSTO", startY);
    const cW = contentW / 5;
    drawField(m, startY, cW - 2, 22, "Base Calc. ICMS", fmtNum(nota.vProd), "right");
    drawField(m + cW, startY, cW - 2, 22, "Valor ICMS", fmtBRL(nota.vICMS), "right");
    drawField(m + (cW * 2), startY, cW - 2, 22, "Base ICMS ST", "0,00", "right");
    drawField(m + (cW * 3), startY, cW - 2, 22, "Valor ICMS ST", fmtBRL(nota.vST), "right");
    drawField(m + (cW * 4), startY, cW, 22, "V. Total Produtos", fmtBRL(nota.vProd), "right");
    startY += 25;
    drawField(m, startY, cW - 2, 22, "Valor Frete", fmtBRL(nota.vFrete), "right");
    drawField(m + cW, startY, cW - 2, 22, "Valor Seguro", fmtBRL(nota.vSeg), "right");
    drawField(m + (cW * 2), startY, cW - 2, 22, "Desconto", fmtBRL(nota.vDesc), "right");
    drawField(m + (cW * 3), startY, cW - 2, 22, "Outras Despesas", fmtBRL(nota.vOutro), "right");
    drawField(m + (cW * 4), startY, cW, 22, "V. Total Nota", fmtBRL(nota.valorOficial), "right");
    startY += 40;

    // TRANSPORTADOR
    drawSectionTitle("TRANSPORTADOR / VOLUMES TRANSPORTADOS", startY);
    drawField(m, startY, contentW * 0.4, 22, "Razão Social", (nota.emitente || "").slice(0, 50), "left");
    drawField(m + (contentW * 0.405), startY, contentW * 0.15, 22, "Frete por Conta", "0-Remetente", "left");
    drawField(m + (contentW * 0.56), startY, contentW * 0.15, 22, "Código ANTT", "", "left");
    drawField(m + (contentW * 0.715), startY, contentW * 0.12, 22, "Placa Veículo", "", "left");
    drawField(m + (contentW * 0.84), startY, contentW * 0.03, 22, "UF", "RJ", "center");
    drawField(m + (contentW * 0.875), startY, contentW * 0.125, 22, "CNPJ/CPF", nota.cnpjEmit, "left");
    startY += 25;
    drawField(m, startY, contentW * 0.4, 22, "Endereço", "", "left");
    drawField(m + (contentW * 0.405), startY, contentW * 0.35, 22, "Município", "", "left");
    drawField(m + (contentW * 0.76), startY, contentW * 0.04, 22, "UF", "", "center");
    drawField(m + (contentW * 0.805), startY, contentW * 0.195, 22, "Inscrição Estadual", "", "left");
    startY += 40;

    // ITENS
    drawSectionTitle("DADOS DOS PRODUTOS / SERVIÇOS", startY);

    autoTable(doc, {
      startY,
      theme: "grid",
      margin: { left: m, right: m },
      head: [["CÓDIGO PRODUTO", "DESCRIÇÃO DO PRODUTO / SERVIÇO", "NCM/SH", "O/CST", "CFOP", "UN", "QUANT", "VALOR UNIT", "VALOR TOTAL", "VALOR DESC", "B.CÁLC ICMS", "VALOR ICMS", "VALOR IPI", "ALÍQ. ICMS", "ALÍQ. IPI"]],
      body: nota.itens.map(it => [
        it.cProd,
        it.xProd + (it.infAdProd ? "\n" + it.infAdProd : ""),
        it.ncm, 
        (it.impostos?.icms?.orig || "0") + (it.impostos?.icms?.cst || (it.impostos?.icms as any)?.csosn || "00"),
        it.cfop, it.uCom,
        fmtNum(it.qCom), fmtNum(it.vUnCom), fmtNum(it.vProd), fmtNum(it.vDesc || 0),
        fmtNum(it.impostos?.icms?.vBC || 0), fmtNum(it.vICMS || 0), fmtNum(it.vIPI || 0),
        (it.impostos?.icms?.pICMS || 0) + "%", (it.impostos?.ipi?.pIPI || 0) + "%"
      ]),
      styles: { fontSize: 5, cellPadding: 2, lineColor: [120, 120, 120], textColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0], fontStyle: "bold", halign: "center", lineWidth: 0.6, lineColor: [120, 120, 120] },
      columnStyles: {
        0: { cellWidth: 40 }, 1: { cellWidth: "auto" }, 2: { cellWidth: 35 },
        3: { cellWidth: 22 }, 4: { cellWidth: 22 }, 5: { cellWidth: 15 },
        6: { cellWidth: 28, halign: "right" }, 7: { cellWidth: 35, halign: "right" },
        8: { cellWidth: 40, halign: "right" }, 9: { cellWidth: 30, halign: "right" },
        10: { cellWidth: 35, halign: "right" }, 11: { cellWidth: 35, halign: "right" },
        12: { cellWidth: 30, halign: "right" }, 13: { cellWidth: 25, halign: "right" },
        14: { cellWidth: 25, halign: "right" }
      }
    });

    let finalY = (doc as any).lastAutoTable.finalY + 22;
    if (finalY > pageHeight - 100) { doc.addPage(); finalY = m + 22; }

    drawSectionTitle("DADOS ADICIONAIS", finalY);
    drawField(m, finalY, contentW * 0.68, 65, "Informações Complementares", (nota.infCpl || "").slice(0, 600), "left", false);
    drawField(m + (contentW * 0.69), finalY, contentW * 0.31, 65, "Reservado ao Fisco", "", "left");

    doc.save(`DANFE_${nota.numero}_${nota.serie}.pdf`);
  }

  // Metadados técnicos extraídos do XML original (best-effort; sem reformatar)
  const meta = useMemo(() => {
    if (!nota?.xmlOriginal) return { protocolo: "", dhRecbto: "", tpAmb: "", versao: "", tpEmis: "", digest: "" };
    const xml = nota.xmlOriginal;
    const grab = (re: RegExp) => xml.match(re)?.[1]?.trim() ?? "";
    return {
      protocolo: grab(/<nProt>([^<]+)<\/nProt>/),
      dhRecbto: grab(/<dhRecbto>([^<]+)<\/dhRecbto>/),
      tpAmb: grab(/<tpAmb>([^<]+)<\/tpAmb>/),
      versao: grab(/<infNFe[^>]*versao="([^"]+)"/),
      tpEmis: grab(/<tpEmis>([^<]+)<\/tpEmis>/),
      digest: grab(/<DigestValue>([^<]+)<\/DigestValue>/),
    };
  }, [nota]);

  if (!nota) return null;

  const ambiente = meta.tpAmb === "1" ? "Produção" : meta.tpAmb === "2" ? "Homologação" : "—";
  const tipoEmissao = meta.tpEmis === "1" ? "Normal" : meta.tpEmis ? `Autorizada em Contingência (${meta.tpEmis})` : "Normal";
  const dataEmissaoFmt = fmtDate(nota.emissao);
  const dataEmissaoCurta = fmtDate(nota.emissao);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[97vw] w-[1400px] max-h-[95vh] overflow-y-auto p-0 border border-slate-300 dark:border-white/10 shadow-2xl bg-slate-50 dark:bg-[linear-gradient(135deg,#050B26_0%,#0A1A4A_45%,#0D2E7A_100%)] backdrop-blur-2xl !rounded-2xl">
        {/* HEADER */}
        <DialogHeader className="px-8 pt-6 pb-5 border-b border-slate-300 dark:border-white/10 sticky top-0 z-20 bg-white/95 dark:bg-[#0a1a4a]/40 backdrop-blur-xl">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex items-start gap-4">
              <div className="size-14 rounded-2xl grid place-items-center bg-blue-50 dark:bg-[linear-gradient(135deg,rgba(51,204,255,0.18),rgba(26,102,204,0.20))] border border-blue-200 dark:border-[#33CCFF]/35 dark:shadow-[0_4px_18px_rgba(51,153,255,0.35)] shrink-0">
                <Receipt className="size-6 text-blue-600 dark:text-[#9DDDFF]" />
              </div>
              <div className="min-w-0 pt-0.5">
                <DialogTitle className="text-xl flex items-center gap-2 flex-wrap font-bold dark:text-white text-slate-800">
                  <span>NF-{nota.modelo} nº</span>
                  <span className="font-extrabold text-blue-700 dark:text-[#33CCFF] dark:drop-shadow-[0_0_8px_rgba(51,204,255,0.4)]">{nota.numero}</span>
                  <span className="opacity-60 font-normal">/ série {nota.serie}</span>
                  <StatusBadge status={nota.notaStatus} size="sm" className="ml-1.5" />
                </DialogTitle>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 font-medium">Emitida em {dataEmissaoFmt}</p>
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-500 dark:text-slate-500 uppercase tracking-wider bg-slate-200 dark:bg-white/5 px-2 py-0.5 rounded border border-slate-300 dark:border-transparent">Chave de acesso</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[13px] tracking-tight font-bold text-slate-800 dark:text-slate-300">{formatChave(nota.chave)}</span>
                    <button onClick={copyChave} className="text-slate-500 hover:text-blue-600 dark:text-slate-500 dark:hover:text-[#33CCFF] transition-colors p-1" title="Copiar chave">
                      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onChangeIndex(index! - 1)} disabled={index === 0} className="bg-white dark:bg-white/5 border-slate-300 dark:border-white/10 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10">
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-xs font-bold text-slate-700 dark:text-slate-300 tabular-nums px-3 bg-slate-100 dark:bg-white/5 rounded-md py-1.5 border border-slate-200 dark:border-white/10">{(index ?? 0) + 1} / {notas.length}</span>
              <Button variant="outline" size="sm" onClick={() => onChangeIndex(index! + 1)} disabled={index === notas.length - 1} className="bg-white dark:bg-white/5 border-slate-300 dark:border-white/10 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10">
                <ChevronRight className="size-4" />
              </Button>
              <div className="w-px h-6 bg-slate-300 dark:bg-white/10 mx-2"></div>
              <Button variant="outline" size="sm" onClick={exportarPDF} className="bg-white dark:bg-white/5 border-slate-300 dark:border-white/10 text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 shadow-sm">
                <Download className="size-4 mr-2" />PDF
              </Button>

              <Button variant="ghost" size="icon" onClick={onClose} className="ml-2 rounded-full hover:bg-slate-200 dark:hover:bg-white/10 text-slate-600 dark:text-white">
                <X className="size-5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="px-8 py-6 bg-slate-100/30 dark:bg-transparent min-h-[50vh]">
          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-[120px] w-full rounded-2xl" />
              <div className="grid grid-cols-12 gap-4">
                <Skeleton className="h-[200px] col-span-3 rounded-2xl" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl" />
                <Skeleton className="h-[200px] col-span-3 rounded-2xl" />
              </div>
              <Skeleton className="h-[400px] w-full rounded-2xl" />
            </div>
          ) : (
            <div className="space-y-5">
              {/* DADOS GERAIS - Condensed Grid */}
              <Block title="Visão Geral do Documento" titleIcon={<ClipboardList className="size-4" />}>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                  <PillField icon={<Tag className="size-4" />} label="Modelo" value={String(nota.modelo)} />
                  <PillField icon={<ClipboardList className="size-4" />} label="Série" value={nota.serie || "—"} />
                  <PillField icon={<Hash className="size-4" />} label="Número" value={nota.numero || "—"} />
                  <PillField icon={<Calendar className="size-4" />} label="Emissão" value={dataEmissaoCurta} />
                  <PillField
                    icon={<FileSignature className="size-4" />}
                    label="Situação Sefaz"
                    custom={<StatusBadge status={nota.notaStatus} size="sm" />}
                  />
                  <PillField icon={<ArrowRightLeft className="size-4" />} label="Tipo" value={(nota.detalhes?.ide as any)?.tpNF === "0" ? "Entrada" : "Saída"} />
                  <PillField icon={<Workflow className="size-4" />} label="Finalidade" value="Normal" />
                  <PillField icon={<Workflow className="size-4" />} label="Natureza" value={nota.natOp || "—"} />
                </div>
              </Block>

              {/* LINHA: EMITENTE | DESTINATÁRIO | CHAVE | TOTAIS */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-3">
                  <PartyBlock
                    icon={<User className="size-4 text-blue-600 dark:text-blue-400" />}
                    title="EMITENTE"
                    nome={nota.emitente}
                    cnpj={nota.cnpjEmit}
                    uf={nota.ufEmit}
                  />
                </div>
                <div className="lg:col-span-3">
                  <PartyBlock
                    icon={<Users className="size-4 text-emerald-600 dark:text-emerald-400" />}
                    title="DESTINATÁRIO"
                    nome={nota.destinatario}
                    cnpj={nota.cnpjDest}
                    uf={nota.ufDest}
                  />
                </div>
                <div className="lg:col-span-3">
                  <Block title="Validação Sistêmica" titleIcon={<KeyRound className="size-4" />}>
                    <div className="flex flex-col gap-2">
                      <div className="bg-slate-100 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-300 dark:border-white/5 text-center">
                        <div className="text-[10px] uppercase font-bold text-slate-600 dark:text-slate-400 mb-1 tracking-wider">Assinatura Digital / Chave</div>
                        <div className="font-mono text-[10px] leading-relaxed tracking-tight break-all text-slate-800 dark:text-slate-300 font-bold">
                          {formatChave(nota.chave)}
                        </div>
                      </div>
                      <PillField icon={<Send className="size-4" />} label="Ambiente Sefaz" value={ambiente} colSpan />
                      <PillField icon={<Code2 className="size-4" />} label="Versão XML" value={meta.versao || "—"} colSpan />
                    </div>
                  </Block>
                </div>
                <div className="lg:col-span-3">
                  <Block title="TOTAIS DA NOTA" titleIcon={<CircleDollarSign className="size-4" />}>
                    <div className="space-y-2">
                      <TotalCard label="Produtos" value={nota.vProd} />
                      <TotalCard label="ICMS" value={nota.vICMS} />
                      <TotalCard label="ICMS ST" value={nota.vST} />
                      <TotalCard label="Total (vNF)" value={nota.valorOficial} highlight />
                    </div>
                  </Block>
                </div>
              </div>

              {/* IMPOSTOS (abas) + METADADOS */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-12">
                  <Block compact>
                    <Tabs defaultValue="itens" className="w-full">
                      <div className="px-4 pt-3 pb-2 border-b border-slate-300 dark:border-white/10 bg-slate-100/50 dark:bg-transparent">
                        <TabsList className="bg-slate-200/70 border border-slate-300 dark:border-none dark:bg-white/5 p-1 rounded-xl">
                          <TabsTrigger value="itens" className="rounded-lg text-xs font-bold px-4 py-1.5 text-slate-700 dark:text-slate-300 data-[state=active]:bg-white dark:data-[state=active]:bg-primary data-[state=active]:text-blue-700 dark:data-[state=active]:text-white data-[state=active]:shadow-sm transition-all">
                            Itens & Tributação Expandida ({nota.itens.length})
                          </TabsTrigger>
                          <TabsTrigger value="impostos" className="rounded-lg text-xs font-bold px-4 py-1.5 text-slate-700 dark:text-slate-300 data-[state=active]:bg-white dark:data-[state=active]:bg-primary data-[state=active]:text-blue-700 dark:data-[state=active]:text-white data-[state=active]:shadow-sm transition-all">
                            Resumo de Impostos
                          </TabsTrigger>
                          <TabsTrigger value="contabil" className="rounded-lg text-xs font-bold px-4 py-1.5 text-slate-700 dark:text-slate-300 data-[state=active]:bg-white dark:data-[state=active]:bg-primary data-[state=active]:text-blue-700 dark:data-[state=active]:text-white data-[state=active]:shadow-sm transition-all">
                            Dados Contábeis
                          </TabsTrigger>
                        </TabsList>
                      </div>

                      <TabsContent value="itens" className="p-0 m-0">
                        <div
                          ref={scrollContainerRef}
                          onMouseEnter={() => { tableHoverRef.current = true; }}
                          onMouseLeave={() => { tableHoverRef.current = false; }}
                          className="overflow-x-auto bg-white dark:bg-transparent custom-scrollbar shadow-inner max-h-[calc(90vh-420px)] min-h-[250px] overflow-y-auto"
                        >
                          <Table wrapperClassName="overflow-visible" className="min-w-[1700px] whitespace-nowrap">
                            <TableHeader className="sticky top-0 bg-gradient-to-r from-slate-950 to-blue-900/60 backdrop-blur-md z-10 border-b border-white/10 shadow-2xl">
                              <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="w-[40px] text-center font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-4 py-4 border-r border-white/10">#</TableHead>
                                <TableHead className="font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-4 py-4 border-r border-white/10 min-w-[280px]">Produto / Serviço</TableHead>
                                <TableHead className="font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-4 py-4 border-r border-white/10">CFOP / NCM</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-4 py-4 border-r border-white/10">Qtd / UN</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-4 py-4 border-r border-white/10">V. Unitário</TableHead>

                                {/* Tributos Estendidos - Mantendo cores sutis mas com o novo estilo */}
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-400 bg-blue-500/10 border-r border-white/10">ICMS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-cyan-400 bg-cyan-500/10 border-r border-white/10">ICMS-ST</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-indigo-400 bg-indigo-500/10 border-r border-white/10">FCP / ST</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-purple-400 bg-purple-500/10 border-r border-white/10">IPI</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-amber-400 bg-amber-500/10 border-r border-white/10">PIS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-orange-400 bg-orange-500/10 border-r border-white/10">COFINS</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-pink-400 bg-pink-500/10 border-r border-white/10">ISSQN</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-rose-400 bg-rose-500/10 border-r border-white/10">IBS / CBS</TableHead>

                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-emerald-400 bg-emerald-500/10">Valor Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-slate-200 dark:divide-white/5">
                              {nota.itens.length === 0 && (
                                <TableRow><TableCell colSpan={14} className="text-center text-slate-500 font-medium py-10">Sem itens registrados na nota.</TableCell></TableRow>
                              )}
                              {nota.itens.map((it) => (
                                <TableRow key={it.nItem} className="hover:bg-[#33CCFF]/10 transition-all group">
                                  <TableCell className="text-[11px] text-slate-500 font-bold text-center border-r border-white/5">{it.nItem}</TableCell>
                                  <TableCell className="max-w-[280px] px-4 py-3 border-r border-white/5">
                                    <div className="font-extrabold text-xs text-slate-800 dark:text-slate-200 truncate" title={it.xProd}>{it.xProd}</div>
                                    <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate opacity-90 font-medium">CÓD: {it.cProd}</div>
                                  </TableCell>
                                  <TableCell className="px-4 py-3 border-r border-white/5">
                                    <div className="text-[11px] font-mono font-bold text-slate-700 dark:text-slate-300 bg-slate-100 border border-slate-200 dark:border-transparent dark:bg-white/10 px-1.5 py-0.5 rounded inline-block">{it.cfop}</div>
                                    <div className="text-[10px] text-slate-600 dark:text-slate-500 font-mono mt-1 font-bold">NCM: {it.ncm}</div>
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-[13px] font-bold text-slate-700 dark:text-slate-300 px-4 py-3 border-r border-white/5">
                                    {fmtNum(it.qCom)} <span className="text-[9px] text-slate-500 uppercase ml-0.5">{it.uCom}</span>
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-xs font-semibold text-slate-700 dark:text-slate-400 px-4 py-3 border-r border-white/5">{fmtBRL(it.vUnCom)}</TableCell>

                                  {/* ICMS */}
                                  <TableCell className="text-right bg-blue-50/70 dark:bg-blue-900/5 group-hover:bg-blue-100/80 dark:group-hover:bg-blue-900/20 transition-colors border-l border-slate-200 dark:border-white/5">
                                    <div className="tabular-nums text-[13px] font-bold text-blue-800 dark:text-blue-300">{fmtBRL(it.vICMS || 0)}</div>
                                    <div className="text-[10px] font-mono text-blue-600/90 dark:text-blue-500/80 mt-0.5 font-bold">CST {it.impostos?.icms?.cst || (it.impostos?.icms as any)?.csosn || "—"}</div>
                                  </TableCell>

                                  {/* ICMS-ST */}
                                  <TableCell className="text-right bg-cyan-50/70 dark:bg-cyan-900/5 group-hover:bg-cyan-100/80 dark:group-hover:bg-cyan-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-cyan-800 dark:text-cyan-300">{fmtBRL(it.impostos?.icmsST?.vICMSST || 0)}</div>
                                    <div className="text-[10px] font-mono text-cyan-600/90 dark:text-cyan-500/80 mt-0.5 font-bold">{it.impostos?.icmsST?.pICMSST ? `${it.impostos.icmsST.pICMSST}%` : "—"}</div>
                                  </TableCell>

                                  {/* FCP */}
                                  <TableCell className="text-right bg-indigo-50/70 dark:bg-indigo-900/5 group-hover:bg-indigo-100/80 dark:group-hover:bg-indigo-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-indigo-800 dark:text-indigo-300">{fmtBRL((it.impostos?.fcp?.vFCP || 0) + (it.impostos?.fcpST?.vFCPST || 0))}</div>
                                    <div className="text-[10px] font-mono text-indigo-600/90 dark:text-indigo-500/80 mt-0.5 font-bold">{it.impostos?.fcp?.pFCP ? `${it.impostos.fcp.pFCP}%` : "—"}</div>
                                  </TableCell>

                                  {/* IPI */}
                                  <TableCell className="text-right bg-purple-50/70 dark:bg-purple-900/5 group-hover:bg-purple-100/80 dark:group-hover:bg-purple-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-purple-800 dark:text-purple-300">{fmtBRL(it.vIPI || 0)}</div>
                                    <div className="text-[10px] font-mono text-purple-600/90 dark:text-purple-500/80 mt-0.5 font-bold">CST {it.impostos?.ipi?.cst || "—"}</div>
                                  </TableCell>

                                  {/* PIS */}
                                  <TableCell className="text-right bg-amber-50/70 dark:bg-amber-900/5 group-hover:bg-amber-100/80 dark:group-hover:bg-amber-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-amber-800 dark:text-amber-300">{fmtBRL(it.vPIS || 0)}</div>
                                    <div className="text-[10px] font-mono text-amber-600/90 dark:text-amber-500/80 mt-0.5 font-bold">CST {it.impostos?.pis?.cst || "—"}</div>
                                  </TableCell>

                                  {/* COFINS */}
                                  <TableCell className="text-right bg-orange-50/70 dark:bg-orange-900/5 group-hover:bg-orange-100/80 dark:group-hover:bg-orange-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-orange-800 dark:text-orange-300">{fmtBRL(it.vCOFINS || 0)}</div>
                                    <div className="text-[10px] font-mono text-orange-600/90 dark:text-orange-500/80 mt-0.5 font-bold">CST {it.impostos?.cofins?.cst || "—"}</div>
                                  </TableCell>

                                  {/* ISSQN */}
                                  <TableCell className="text-right bg-pink-50/70 dark:bg-pink-900/5 group-hover:bg-pink-100/80 dark:group-hover:bg-pink-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-pink-800 dark:text-pink-300">{fmtBRL(it.impostos?.issqn?.vISSQN || 0)}</div>
                                  </TableCell>

                                  {/* IBS/CBS */}
                                  <TableCell className="text-right bg-rose-50/70 dark:bg-rose-900/5 group-hover:bg-rose-100/80 dark:group-hover:bg-rose-900/20 transition-colors">
                                    <div className="tabular-nums text-[13px] font-bold text-rose-800 dark:text-rose-300">{fmtBRL((it.impostos?.ibsCbs?.vIBS || 0) + (it.impostos?.ibsCbs?.vCBS || 0))}</div>
                                  </TableCell>

                                  <TableCell className="text-right tabular-nums text-[14px] font-black text-emerald-700 dark:text-emerald-400 border-l border-slate-200 dark:border-white/5">
                                    {fmtBRL(it.vProd)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>

                      <TabsContent value="impostos" className="p-4 m-0">
                        <div className="rounded-xl border border-slate-300 dark:border-white/10 overflow-hidden bg-white dark:bg-white/5 shadow-sm">
                          <Table>
                            <TableHeader className="bg-gradient-to-r from-slate-950 to-blue-900/40 border-b border-white/10">
                              <TableRow className="hover:bg-transparent border-none">
                                <TableHead className="font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-6 py-4 border-r border-white/10">Imposto / Tributo</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-6 py-4 border-r border-white/10">Base de cálculo</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-6 py-4 border-r border-white/10">Alíquota Média %</TableHead>
                                <TableHead className="text-right font-black text-[9px] uppercase tracking-[0.2em] text-blue-300 px-6 py-4">Valor Apurado</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-slate-200 dark:divide-white/5">
                              {nota.impostosDetalhe.length === 0 && (
                                <TableRow><TableCell colSpan={4} className="text-center text-slate-500 font-medium py-8">Nenhum imposto consolidado.</TableCell></TableRow>
                              )}
                              {nota.impostosDetalhe.map((i, idx) => (
                                <TableRow key={idx} className="hover:bg-[#33CCFF]/10 transition-all">
                                  <TableCell className="font-extrabold text-slate-800 dark:text-white px-6 py-3 border-r border-white/5">{i.sigla}</TableCell>
                                  <TableCell className="text-right num font-semibold text-slate-700 dark:text-slate-300 px-6 py-3 border-r border-white/5">{fmtBRL(i.base)}</TableCell>
                                  <TableCell className="text-right num text-slate-600 dark:text-slate-300 px-6 py-3 border-r border-white/5">
                                    <span className="bg-slate-200 dark:bg-white/10 px-2 py-0.5 rounded text-xs font-bold">{i.aliquota.toFixed(2)}%</span>
                                  </TableCell>
                                  <TableCell className="text-right num font-black text-[15px] text-slate-800 dark:text-white px-6 py-3">{fmtBRL(i.valor)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>

                      <TabsContent value="contabil" className="p-4 m-0">
                        <div className="rounded-xl border border-slate-300 dark:border-white/10 p-5 space-y-4 bg-white dark:bg-white/5 shadow-sm">
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Valor Contábil" value={fmtBRL(nota.valorOficial)} />
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Valor declarado (vNF)" value={nota.valorDeclarado != null ? fmtBRL(nota.valorDeclarado) : "—"} />
                            <PillField icon={<CircleDollarSign className="size-4" />} label="Soma dos itens (vProd)" value={fmtBRL(nota.valorItens)} />
                            <PillField icon={<Tag className="size-4" />} label="Fonte da Extração" value={nota.fonte} />
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
                            <PillField
                              icon={<ShieldCheck className="size-4" />}
                              label="Protocolo de autorização Sefaz"
                              value={meta.protocolo || "—"}
                              sub={meta.dhRecbto ? formatDh(meta.dhRecbto) : ""}
                            />
                            <PillField icon={<Fingerprint className="size-4" />} label="Digest Value (Assinatura)" value={meta.digest || "—"} />
                          </div>

                          {nota.infCpl && (
                            <div className="pt-4 mt-2 border-t border-slate-300 dark:border-white/10">
                              <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2 flex items-center gap-2">
                                <FileText className="size-3" /> Informações Complementares (Fisco/Contribuinte)
                              </p>
                              <div className="bg-amber-100 dark:bg-amber-900/10 border border-amber-300 dark:border-amber-900/30 p-4 rounded-lg text-xs whitespace-pre-wrap text-amber-900 dark:text-amber-200 font-bold leading-relaxed shadow-inner">
                                {nota.infCpl}
                              </div>
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </Block>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function formatChave(c: string) {
  if (!c) return "—";
  return c.replace(/(.{4})/g, "$1 ").trim();
}

function formatDh(s: string) {
  try {
    const dt = new Date(s);
    if (isNaN(dt.getTime())) return s;
    return dt.toLocaleString("pt-BR");
  } catch { return s; }
}

/* =========================================================
   Componentes visuais — pílulas e blocos translúcidos
   ========================================================= */

function Block({
  title, titleIcon, children, compact,
}: {
  title?: string; titleIcon?: React.ReactNode; children: React.ReactNode; compact?: boolean;
}) {
  return (
    <section className={`bg-white dark:bg-[#0d214f]/70 border border-slate-200 dark:border-[#33CCFF]/30 rounded-2xl shadow-sm dark:shadow-2xl dark:shadow-blue-900/20 ${compact ? "p-0 overflow-hidden" : "p-4"}`}>
      {title && (
        <div className="flex items-center gap-2 mb-3 text-slate-800 dark:text-[#33CCFF]">
          {titleIcon}
          <h3 className="text-[11px] uppercase tracking-widest font-extrabold">{title}</h3>
        </div>
      )}
      {children}
    </section>
  );
}

function PillField({
  icon, label, value, sub, custom, colSpan,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string;
  sub?: string;
  custom?: React.ReactNode;
  colSpan?: boolean;
}) {
  return (
    <div className={`pill px-3 py-2 flex items-center gap-3 border border-slate-300 dark:border-[#33CCFF]/20 hover:border-blue-400 dark:hover:border-[#33CCFF]/50 transition-colors rounded-xl shadow-sm dark:shadow-none ${colSpan ? "col-span-2" : ""}`}>
      {icon && (
        <div className="size-7 shrink-0 rounded-lg grid place-items-center bg-white dark:bg-primary/10 border border-slate-200 dark:border-primary/20 text-slate-600 dark:text-[#33CCFF] shadow-sm">
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[8px] uppercase font-extrabold tracking-wider text-slate-600 dark:text-slate-400 leading-tight mb-0.5">{label}</p>
        {custom ? (
          <div className="mt-0.5">{custom}</div>
        ) : (
          <p className="text-[12px] font-extrabold text-slate-800 dark:text-white leading-tight truncate" title={value}>{value || "—"}</p>
        )}
        {sub && <p className="text-[9px] font-bold text-slate-500 dark:text-slate-400 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function PartyBlock({
  icon, title, nome, cnpj, uf,
}: {
  icon: React.ReactNode; title: string; nome: string; cnpj: string; uf: string;
}) {
  return (
    <Block title={title} titleIcon={icon}>
      <div className="space-y-3 text-sm">
        <div className="min-h-[40px]">
          <p className="text-[8px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-400">Nome / Razão Social</p>
          <p className="font-extrabold text-slate-800 dark:text-white leading-snug mt-1 text-xs line-clamp-2">{nome || "—"}</p>
        </div>
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 dark:border-white/10">
          <div className="min-w-0 flex-1">
            <p className="text-[8px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-400">CNPJ</p>
            <p className="font-mono font-bold text-slate-700 dark:text-slate-300 mt-1 text-[10px] truncate">{cnpj || "—"}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[8px] font-extrabold uppercase tracking-wider text-slate-600 dark:text-slate-400">UF</p>
            <p className="font-extrabold text-slate-700 dark:text-slate-300 mt-1 text-[11px]">{uf || "—"}</p>
          </div>
        </div>
      </div>
    </Block>
  );
}

function TotalCard({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={`pill px-3 py-2 flex justify-between items-center rounded-xl ${highlight
          ? "border-blue-400 dark:border-[#33CCFF]/55 bg-blue-100 dark:bg-[#0d2e7a] shadow-md dark:shadow-[0_0_25px_rgba(51,204,255,0.25)]"
          : "bg-slate-50 dark:bg-[#0d214f]/60 border-slate-300 dark:border-white/10 shadow-sm dark:shadow-none"
        }`}
    >
      <p className={`text-[9px] font-extrabold uppercase tracking-wider leading-tight ${highlight ? "text-blue-900 dark:text-white" : "text-slate-600 dark:text-slate-400"}`}>
        {label}
      </p>
      <p className={`tabular-nums font-black ${highlight ? "text-[16px] text-blue-800 dark:text-[#33CCFF] dark:drop-shadow-[0_0_10px_rgba(51,204,255,0.4)]" : "text-[13px] text-slate-800 dark:text-white"}`}>
        {fmtBRL(value)}
      </p>
    </div>
  );
}
